import { useEffect, useState } from "react";
import { toast } from "sonner";
import { jobsApi } from "@/lib/admin-api";
import type { RunLogEntry } from "@/lib/jobs-types";
import { loadCompleteLog } from "@/lib/run-tail";
import { TAIL_PAGE_LIMIT, TAIL_STALE_REQUEST_MS } from "@/hooks/use-run-tail";
import { isAbortError, useBoundedRequest } from "@/hooks/use-bounded-request";

export type ReplayLog = {
  status: "loading" | "ready" | "error";
  entries: RunLogEntry[];
  /** True only when the tail reported `finished`; false while loading and on error. */
  final: boolean;
};

const LOADING: ReplayLog = { status: "loading", entries: [], final: false };

/**
 * Loads one finished run's complete log once, for replay (sc-114198). Every page goes through the live
 * panel's cursor `tail` endpoint (its `admin-tail` budget) with the same per-request deadline, and the load
 * stops at the log's end — it never polls. A failure toasts once. Closing the panel mid-load cancels it:
 * no further page is requested and nothing commits.
 */
export function useReplayLog(jobId: string): ReplayLog {
  const [log, setLog] = useState<ReplayLog>(LOADING);
  const { run } = useBoundedRequest(TAIL_STALE_REQUEST_MS);

  useEffect(() => {
    let cancelled = false;

    loadCompleteLog(
      (after) => run((signal) => jobsApi.tail(jobId, after, TAIL_PAGE_LIMIT, signal)),
      TAIL_PAGE_LIMIT,
      () => cancelled
    ).then(
      (result) => {
        if (result === null || cancelled) return;
        setLog({ status: "ready", entries: result.entries, final: result.final });
      },
      (err: unknown) => {
        if (cancelled) return;
        setLog({ status: "error", entries: [], final: false });
        toast.error(
          err instanceof Error && !isAbortError(err) ? err.message : "Failed to load the run log"
        );
      }
    );

    return () => {
      cancelled = true;
    };
  }, [jobId, run]);

  return log;
}
